import { FC, useContext, useEffect, useRef, useState } from "react";
import { useMutation } from "react-query";
import { User } from "src/api/types";
import { APISomethingWrongMsg, NetworkDisconnectMsg } from "src/lib/constants";
import { getErrorMessage, logger } from "src/lib/utils";
import client from "../../api";
import GlobalContext from "src/context/global-context";
import blurImage from "../../assets/images/dark-blur-blurred-gradient.png";

type Props = {
  user: User;
};

type KycDocs = {
  document_front_path?: string | null;
  document_back_path?: string | null;
  face_liveness_path?: string | null;
};

type ImageType = "front" | "back" | "face";

const UserDocsTab: FC<Props> = ({ user }) => {
  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);
  const [dataSource, setDataSource] = useState<KycDocs | null>(null);

  const imageTimersRef = useRef<Record<ImageType, NodeJS.Timeout | null>>({
    front: null,
    back: null,
    face: null,
  });
  const [images, setImages] = useState<Record<ImageType, string | null>>({
    front: null,
    back: null,
    face: null,
  });

  const [loadingImage, setLoadingImage] = useState<ImageType | null>(null);

  /* ---------------- COMMON ERROR ---------------- */

  const handleError = (error: any) => {
    if (error.code === "ERR_NETWORK") {
      setAlertText(NetworkDisconnectMsg);
    } else if (error.response) {
      setAlertText(getErrorMessage(error.response.data));
    } else {
      setAlertText(APISomethingWrongMsg);
    }
    setIsAlertOpened(true);
  };

  /* ---------------- GET DOC PATHS ---------------- */

  const { mutate: getUserKycInfo, isLoading } = useMutation(
    client.newUserKycInfo.getKycInfoDocs,
    {
      onSuccess: (data) => {
        setDataSource(data?.data || null);
        if (data?.message === "You do not have access for this route!") {
          setAlertText(data?.message);
          setIsAlertOpened(true);
        }
      },
      onError: handleError,
    },
  );

  /* ---------------- GET ACTUAL IMAGE ---------------- */

  const { mutate: getUserKycImages } = useMutation(
    client.newUserKycInfo.getKycInfoImages,
    {
      onSuccess: (data, variables: any) => {
        const type: ImageType = variables.key;
        const imageUrl = data?.data?.signedUrl;

        /* clear previous timer if exists */
        if (imageTimersRef.current[type]) {
          clearTimeout(imageTimersRef.current[type]!);
        }

        /* set image */
        setImages((prev) => ({
          ...prev,
          [type]: imageUrl,
        }));

        setLoadingImage(null);

        /* auto remove image after 1 minute */
        imageTimersRef.current[type] = setTimeout(() => {
          setImages((prev) => ({
            ...prev,
            [type]: null,
          }));
        }, 60000);

        if (data?.message === "You do not have access for this route!") {
          setAlertText(data?.message);
          setIsAlertOpened(true);
        }
      },
      onError: (error: any) => {
        setLoadingImage(null);
        handleError(error);
      },
    },
  );

  useEffect(() => {
    if (user?.id) {
      getUserKycInfo({ id: user.id });
    }
  }, [user?.id]);

  useEffect(() => {
    return () => {
      Object.values(imageTimersRef.current).forEach((timer) => {
        if (timer) clearTimeout(timer);
      });
    };
  }, []);

  /* ---------------- HELPERS ---------------- */

  const hasValue = (value?: string | null) =>
    value !== null && value !== undefined && value !== "";

  const fetchImage = (type: ImageType) => {
    if (!user?.id) return;

    setLoadingImage(type);

    getUserKycImages({
      id: user.id,
      key: type, // "front" | "back" | "face"
    });
  };

  /* ---------------- IMAGE CARD ---------------- */

  const ImageCard = ({
    label,
    path,
    type,
  }: {
    label: string;
    path?: string | null;
    type: ImageType;
  }) => {
    const imageExists = hasValue(path);
    const fetchedImage = images[type];
    const isLoadingThis = loadingImage === type;

    return (
      <div className="flex flex-col gap-3 w-full">
        <p className="font-medium text-sm">{label}</p>
        <div className="w-full h-[520px] border border-gray-300 rounded-xl overflow-hidden bg-gray-200 relative shadow-sm">
          {!imageExists ? (
            <div className="text-gray-400 text-sm">Image not available</div>
          ) : fetchedImage ? (
            /* ---------- actual image ---------- */
            <img
              src={fetchedImage}
              alt={label}
              className="absolute inset-0 w-full h-full object-contain bg-black"
            />
          ) : (
            /* ---------- blurred placeholder ---------- */
            <div
              onClick={() => fetchImage(type)}
              className="cursor-pointer absolute inset-0"
            >
              {isLoadingThis ? (
                <div className="w-full h-full flex items-center justify-center text-sm">
                  Loading...
                </div>
              ) : (
                <>
                  {/* blur image background */}
                  <img
                    src={blurImage}
                    alt="blur"
                    className="absolute inset-0 w-full h-full object-cover"
                  />

                  {/* optional extra blur effect */}
                  <div className="absolute inset-0 backdrop-blur-md bg-black/10" />

                  {/* click label */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="px-3 py-1 text-sm font-medium bg-black/70 text-white rounded-md">
                      Click to view
                    </span>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  /* ---------------- UI ---------------- */

  if (isLoading) {
    return <div className="p-4">Loading documents...</div>;
  }

  return (
    <div>
      <h2 className="text-lg font-semibold mb-6">User Documents</h2>

      <div className="flex flex-col gap-12 w-full px-6">
        <ImageCard
          label="Document Front"
          path={dataSource?.document_front_path}
          type="front"
        />
        <ImageCard
          label="Document Back"
          path={dataSource?.document_back_path}
          type="back"
        />
        <ImageCard
          label="Face Liveness"
          path={dataSource?.face_liveness_path}
          type="face"
        />
      </div>
    </div>
  );
};

export default UserDocsTab;
